import React, { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, Row, Col } from 'reactstrap';
import axios from 'axios';
import { SUBSCRIPTION_PLANS, BUNDLES, getBundlePrice, getPlansForSpecialty } from 'config/subscriptionPlans';
import { getSpecialty } from 'config/specialtyConfig';

const RegistrationPaymentStep = ({ email, onComplete, onBack, hasGroupCode = false, onPlanModeChange, specialty = 'pmr' }) => {
  const stripe = useStripe();
  const elements = useElements();

  // Must be the specialty's own plans: the id chosen here becomes selectedPlanId,
  // which decides what the end-of-trial charge activates. Using the base list
  // emitted a pmrPrep id for every signup, so Pain/Sports registrants were
  // charged for PM&R.
  const plans = getPlansForSpecialty(specialty);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [planMode, setPlanMode] = useState('single');
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundleDuration, setBundleDuration] = useState(12);

  const getBtnColorClass = (color, isSelected) => {
    return isSelected ? `btn-${color}` : `btn-outline-${color}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (planMode === 'single' && !selectedPlan) {
      setError('Please select a plan.');
      return;
    }

    if (planMode === 'bundle' && !selectedBundle) {
      setError('Please select a bundle.');
      return;
    }

    if (!stripe || !elements) {
      setError('Payment system is still loading. Please wait.');
      return;
    }

    setLoading(true);

    try {
      // 1. Validate card input
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      // 2. Create SetupIntent on backend
      const { data } = await axios.post('/createTrialSetup', { email });
      const { clientSecret, customerId } = data;

      // 3. Confirm setup (saves card, no charge)
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.origin + '/auth/register',
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message);
        setLoading(false);
        return;
      }

      // 4. Pass results back to Register
      const result = {
        customerId,
        paymentMethodId: setupIntent.payment_method,
        selectedPlanId: planMode === 'bundle'
          ? SUBSCRIPTION_PLANS.find(p => p.months === bundleDuration)?.id || selectedPlan
          : selectedPlan,
      };
      if (planMode === 'bundle' && selectedBundle) {
        result.bundleId = selectedBundle;
      }
      onComplete(result);
    } catch (err) {
      console.error('Payment setup error:', err);
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h6 className="text-center text-muted mb-3" style={{ fontSize: 13 }}>
          Choose your plan (you won't be charged until your trial ends)
        </h6>

        {/* Plan mode toggle — only show when no group code */}
        {!hasGroupCode && (
          <div className="d-flex justify-content-center mb-3">
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${planMode === 'single' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setPlanMode('single'); setSelectedBundle(null); onPlanModeChange?.('single'); }}
                style={{ fontSize: '0.8rem' }}
              >
                Single Specialty
              </button>
              <button
                type="button"
                className={`btn ${planMode === 'bundle' ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => { setPlanMode('bundle'); setSelectedPlan(null); onPlanModeChange?.('bundle'); }}
                style={{ fontSize: '0.8rem' }}
              >
                Bundle & Save
              </button>
            </div>
          </div>
        )}

        {planMode === 'single' && (
          <>
            <Row>
              {plans.map((plan) => (
                <Col xs="6" className="mb-2" key={plan.id}>
                  <button
                    type="button"
                    className={`btn ${getBtnColorClass(plan.color, selectedPlan === plan.id)} w-100`}
                    onClick={() => setSelectedPlan(plan.id)}
                    style={{ fontSize: '0.82rem', padding: '10px 6px' }}
                  >
                    {plan.months} mo - ${plan.price}
                    {plan.popular && <span className="badge badge-warning ml-1">Best</span>}
                  </button>
                </Col>
              ))}
            </Row>
            {selectedPlan && (
              <div className="text-center mt-2">
                <small className="text-muted">
                  After your 14-day free trial, your card will be charged <strong>${plans.find(p => p.id === selectedPlan)?.price}</strong> for{' '}
                  <strong>{plans.find(p => p.id === selectedPlan)?.months} months</strong> of access.
                </small>
              </div>
            )}
          </>
        )}

        {planMode === 'bundle' && (
          <>
            {/* Duration selector */}
            <div className="d-flex justify-content-center flex-wrap mb-3" style={{ gap: '6px' }}>
              {SUBSCRIPTION_PLANS.filter(p => ![48].includes(p.months)).map((p) => (
                <button
                  key={p.months}
                  type="button"
                  className={`btn btn-sm ${bundleDuration === p.months ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setBundleDuration(p.months)}
                  style={{ fontSize: '0.75rem' }}
                >
                  {p.months} mo
                </button>
              ))}
            </div>

            {/* Bundle cards */}
            {BUNDLES.map((bundle) => {
              const basePlan = SUBSCRIPTION_PLANS.find(p => p.months === bundleDuration);
              if (!basePlan) return null;
              const pricing = getBundlePrice(basePlan, bundle);
              const isSelected = selectedBundle === bundle.id;
              return (
                <button
                  key={bundle.id}
                  type="button"
                  className={`btn w-100 mb-2 text-left ${isSelected ? `btn-${bundle.color}` : `btn-outline-${bundle.color}`}`}
                  onClick={() => setSelectedBundle(bundle.id)}
                  style={{ fontSize: '0.82rem', padding: '10px 12px' }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{bundle.name}</strong>
                      <div style={{ fontSize: '0.72rem', opacity: 0.8 }}>
                        {bundle.specialties.map(s => getSpecialty(s).name).join(' + ')}
                      </div>
                    </div>
                    <div className="text-right">
                      <strong>${pricing.totalPrice}</strong>
                      <span className="ml-1" style={{ textDecoration: 'line-through', opacity: 0.55 }}>${pricing.individualTotal}</span>
                      <div style={{ fontSize: '0.7rem' }}>
                        <span className={isSelected ? 'text-white' : 'text-success'}>Save {pricing.discountPercent}%</span>
                        {bundle.popular && <span className="badge badge-danger ml-1" style={{ fontSize: '0.6rem' }}>Best</span>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {selectedBundle && (() => {
              const basePlan = SUBSCRIPTION_PLANS.find(p => p.months === bundleDuration);
              const bundle = BUNDLES.find(b => b.id === selectedBundle);
              if (!basePlan || !bundle) return null;
              const pricing = getBundlePrice(basePlan, bundle);
              return (
                <div className="text-center mt-2">
                  <small className="text-muted">
                    After your 14-day free trial, your card will be charged <strong>${pricing.totalPrice}</strong> for{' '}
                    <strong>{bundleDuration} months</strong> of access to {bundle.name}.
                  </small>
                </div>
              );
            })()}
          </>
        )}
      </div>

      <div className="mb-3">
        <h6 className="text-muted mb-3" style={{ fontSize: 13 }}>
          Payment method
        </h6>
        <PaymentElement />
      </div>

      {error && (
        <div className="text-center mb-3">
          <small className="text-danger">{error}</small>
        </div>
      )}

      <div className="text-center d-flex justify-content-center" style={{ gap: '12px' }}>
        <Button className="mt-2" color="secondary" type="button" onClick={onBack} disabled={loading}>
          <i className="fas fa-arrow-left mr-1" /> Back
        </Button>
        <Button className="mt-2" color="success" type="button" onClick={handleSubmit} disabled={loading || !stripe}>
          {loading ? (
            <><i className="fas fa-spinner fa-spin mr-1" /> Processing...</>
          ) : (
            <>Start Free Trial</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default RegistrationPaymentStep;
